<template>
  <v-card dark class="show opportunity" v-if="opportunity">
    <can I="read" a="Opportunity">
      <v-card-title primary-title>
        <div>
          <div class="headline">{{ opportunity.name }}</div>
          <span v-if="opportunity.organization">{{ opportunity.organization.name }}</span>
        </div>
        <v-spacer />
        <v-tooltip left>
          <v-icon slot="activator" :color="opportunity.coverImage ? 'success' : 'warning'">
            mdi-file-image
          </v-icon>
          <span>Cover image {{ opportunity.coverImage ? 'uploaded' : 'missing' }}</span>
        </v-tooltip>
      </v-card-title>
      <v-divider />
      <table class="v-table theme--dark">
        <tbody>
          <tr>
            <th class="text-xs-right">ID</th>
            <td>{{ opportunity.id }}</td>
          </tr>
          <tr>
            <th class="text-xs-right">Name</th>
            <td>{{ opportunity.name }}</td>
          </tr>
          <tr>
            <th class="text-xs-right">Organization</th>
            <td v-if="opportunity.organization">
              <router-link :to="`/organizations/${opportunity.organization.id}`">
                {{ opportunity.organization.name }}
              </router-link>
            </td>
          </tr>
          <tr>
            <th class="text-xs-right">Slug</th>
            <td>{{ opportunity.slug }}</td>
          </tr>
          <tr>
            <th class="text-xs-right">Set</th>
            <td v-if="opportunity.set">{{ opportunity.set }}</td>
            <td v-else><em>None</em></td>
          </tr>
          <tr>
            <th class="text-xs-right">Type</th>
            <td v-if="opportunity.type">{{ opportunity.type | capitalize }}</td>
            <td v-else><em>None</em></td>
          </tr>
          <tr>
            <th class="text-xs-right">Eligible Profiles</th>
            <td>{{ opportunity.eligibleProfiles }}</td>
          </tr>
          <tr>
            <th class="text-xs-right">Application Count</th>
            <td>{{ opportunity.applicationCount }}</td>
          </tr>
          <tr>
            <th class="text-xs-right">
              {{ statusLabel(opportunityDates.opensAt, 'Opens', 'Opened') }}
            </th>
            <td v-if="opportunityDates.opensAt">
              {{ opportunityDates.opensAt | date('do MMMM yyyy') }}
              <span v-if="hasDisplayTime(opportunityDates.opensAt)">
                at {{ opportunityDates.opensAt | date('HH:mm') }}
              </span>
            </td>
            <td v-else><em>Not set</em></td>
          </tr>
          <tr>
            <th class="text-xs-right">
              {{ statusLabel(opportunityDates.closesAt, 'Closes', 'Closed') }}
            </th>
            <td v-if="opportunityDates.closesAt">
              {{ opportunityDates.closesAt | date('do MMMM yyyy') }}
              <span v-if="hasDisplayTime(opportunityDates.closesAt)">
                at {{ opportunityDates.closesAt | date('HH:mm') }}
              </span>
            </td>
            <td v-else><em>Not set</em></td>
          </tr>
          <tr>
            <th class="text-xs-right">
              {{ statusLabel(opportunityDates.commencesAt, 'Commences', 'Commenced') }}
            </th>
            <td v-if="opportunityDates.commencesAt">
              {{ opportunityDates.commencesAt | date('do MMMM yyyy') }}
              <span v-if="hasDisplayTime(opportunityDates.commencesAt)">
                at {{ opportunityDates.commencesAt | date('HH:mm') }}
              </span>
            </td>
            <td v-else><em>Not set</em></td>
          </tr>
          <tr v-if="opportunity.remuneration">
            <th class="text-xs-right">Remuneration</th>
            <td v-if="opportunity.remuneration.amount">
              {{ opportunity.remuneration.amount | currency(opportunity.remuneration.currency) }}
              {{ frequencyDescriptor(opportunity.remuneration.frequency) }}
            </td>
            <td v-else><em>Not set</em></td>
          </tr>
          <tr v-if="opportunity.remuneration">
            <th class="text-xs-right">Remuneration Disclosed</th>
            <td v-if="opportunity.remuneration.disclosed == null">
              <em>Not set</em>
            </td>
            <td v-else>{{ opportunity.remuneration.disclosed ? 'Yes' : 'No' }}</td>
          </tr>
          <tr v-if="opportunity.behavioralCompetencies">
            <th class="text-xs-right">Personal Traits</th>
            <td v-if="opportunity.behavioralCompetencies.length > 0">
              <v-tooltip
                top
                max-width="400"
                v-for="trait in opportunity.behavioralCompetencies"
                :key="`trait-${trait.id}`"
              >
                <v-chip slot="activator">{{ trait.name }}</v-chip>
                <span>{{ trait.description }}</span>
              </v-tooltip>
            </td>
            <td v-else><em>None</em></td>
          </tr>
          <tr v-if="opportunity.technicalCompetencies">
            <th class="text-xs-right">Technical Competencies</th>
            <td v-if="opportunity.technicalCompetencies.length > 0">
              <v-chip v-for="competency in opportunity.technicalCompetencies" :key="competency">{{
                competency
              }}</v-chip>
            </td>
            <td v-else><em>None</em></td>
          </tr>
          <tr v-if="opportunity.professionalTools">
            <th class="text-xs-right">Professional Tools</th>
            <td v-if="opportunity.professionalTools.length > 0">
              <v-chip v-for="tool in opportunity.professionalTools" :key="tool">{{ tool }}</v-chip>
            </td>
            <td v-else><em>None</em></td>
          </tr>
          <tr>
            <th class="text-xs-right">Promotional Video</th>
            <td v-if="opportunity.video">
              <a :href="opportunity.video" target="_blank">
                {{ opportunity.video }}
              </a>
            </td>
            <td v-else><em>Not specified</em></td>
          </tr>
          <tr>
            <th class="text-xs-right">Published</th>
            <td v-if="opportunityDates.publishedAt">
              {{ opportunityDates.publishedAt | date('h:mm a, MMMM do yyyy') }}
              ({{ opportunityDates.publishedAt | inWords({ addSuffix: true }) }})
            </td>
            <td v-else><em>Not specified</em></td>
          </tr>
          <tr>
            <th class="text-xs-right">Created</th>
            <td v-if="opportunityDates.createdAt">
              {{ opportunityDates.createdAt | date('h:mm a, MMMM do yyyy') }}
              ({{ opportunityDates.createdAt | inWords({ addSuffix: true }) }})
            </td>
            <td v-else><em>Not specified</em></td>
          </tr>
          <tr>
            <th class="text-xs-right">Last updated</th>
            <td v-if="opportunityDates.updatedAt">
              {{ opportunityDates.updatedAt | date('h:mm a, MMMM do yyyy') }}
              ({{ opportunityDates.updatedAt | inWords({ addSuffix: true }) }})
            </td>
            <td v-else><em>Not specified</em></td>
          </tr>
        </tbody>
      </table>

      <v-divider v-if="opportunity.summary && opportunity.summary.length" />
      <v-card-text v-if="opportunity.summary && opportunity.summary.length">
        <div>Summary</div>
        <markdown :html="false" :source="opportunity.summary || ''" class="markdown" />
      </v-card-text>

      <v-divider v-if="opportunity.blurb && opportunity.blurb.length" />
      <v-card-text v-if="opportunity.blurb && opportunity.blurb.length">
        <div>Blurb</div>
        <p v-html="opportunity.blurb"></p>
      </v-card-text>

      <v-divider v-if="opportunity.perks" />
      <v-card-text v-if="opportunity.perks">
        <div>Perks</div>
        <ul class="ml-3">
          <li v-for="perk in opportunity.perks" v-text="perk" :key="perk" />
        </ul>
      </v-card-text>

      <v-divider v-if="opportunity.offerings" />
      <v-card-text v-if="opportunity.offerings">
        <div>Key Offerings</div>
        <ul class="ml-3">
          <li v-for="offering in opportunity.offerings" v-text="offering" :key="offering" />
        </ul>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <can I="delete" a="Opportunity">
          <v-dialog v-model="dialog" lazy absolute max-width="600">
            <v-btn flat slot="activator" color="error">
              <v-icon left>mdi-delete</v-icon> Delete
            </v-btn>

            <v-card>
              <v-card-title class="headline">Are you sure?</v-card-title>
              <v-card-text>
                Deleting an opportunity cannot be undone and will remove all associated data such as
                eligibilities and applications. It is not recommended for any opportunity that has
                been active.
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn flat color="grey" @click.native="dialog = false">cancel</v-btn>
                <v-btn color="error" @click="destroy">Delete</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </can>
        <can I="edit" a="Opportunity">
          <v-btn @click="$emit('edit')" color="primary">
            <v-icon left>mdi-pencil</v-icon> Edit
          </v-btn>
        </can>
      </v-card-actions>
    </can>
  </v-card>
</template>

<script>
// Components
import Markdown from 'vue-markdown'

// Vue Component
export default {
  components: { Markdown },
  data() {
    return { dialog: false }
  },
  computed: {
    opportunity() {
      return this.$store.getters['admin/opportunities/find']()
    },
    opportunityDates() {
      const opportunity = this.opportunity || {}

      return {
        opensAt: this.parseDate(opportunity.opensAt),
        closesAt: this.parseDate(opportunity.closesAt),
        commencesAt: this.parseDate(opportunity.commencesAt),
        publishedAt: this.parseDate(opportunity.publishedAt),
        createdAt: this.parseDate(opportunity.createdAt),
        updatedAt: this.parseDate(opportunity.updatedAt),
      }
    },
  },
  methods: {
    destroy() {
      this.$emit('destroy')
    },
    frequencyDescriptor(frequency) {
      return {
        1: 'Yearly',
        12: 'Monthly',
        52: 'Weekly',
      }[frequency]
    },
    hasDisplayTime(date) {
      return !!date && date.getHours() > 0
    },
    parseDate(value) {
      if (!value) return null

      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? null : date
    },
    statusLabel(date, futureLabel, pastLabel) {
      if (!date) return futureLabel

      return date < new Date() ? pastLabel : futureLabel
    },
  },
}
</script>
